import { airportById } from '../data/airports';
import { hubWeeklyCost } from '../simulation/economy';
import { DEFAULT_NEW_GAME_OPTIONS, scenarioFor, type NewGameOptions } from './scenarios';
import { isStartingAirport } from './airportUnlocks';
import type { CabinClass, GameState, RouteStats } from './types';

export const CURRENT_SAVE_VERSION = 18;
export const SIMULATION_VERSION = 1;
export const DEFAULT_COMPANY_NAME = '凌云航空';

export const COMPANY_NAMES = [
  '凌云航空', '鲲鹏航空', '天穹航空', '星辰航空', '飞越航空',
  '长风航空', '远翔航空', '银翼航空', '天路航空', '云翼航空',
  '极速航空', '飞鹰航空', '天际航空', '卓越航空', '鹏程航空',
  '翼展航空', '九天航空', '瀚海航空', '乘风航空', '翔宇航空',
];

function randomCompanyName(seed: number): string {
  return COMPANY_NAMES[seed % COMPANY_NAMES.length];
}

export function emptyRouteStats(distanceKm = 0): RouteStats {
  const cabins = {} as Record<CabinClass, { seats: number; demand: number; passengers: number; revenue: number; loadFactor: number; unmetDemand: number }>;
  for (const cabin of ['economy', 'business', 'first'] as CabinClass[]) {
    cabins[cabin] = { seats: 0, demand: 0, passengers: 0, revenue: 0, loadFactor: 0, unmetDemand: 0 };
  }
  return {
    distanceKm,
    roundTrips: 0,
    seats: 0,
    demand: 0,
    passengers: 0,
    localPassengers: 0,
    transferPassengers: 0,
    loadFactor: 0,
    unmetDemand: 0,
    revenue: 0,
    cashCost: 0,
    operatingProfit: 0,
    networkContribution: 0,
    cabins,
  };
}

export function createInitialGame(seed = 20_260_721, options: NewGameOptions = { ...DEFAULT_NEW_GAME_OPTIONS, seed }): GameState {
  const scenario = scenarioFor(options.scenarioId);
  const now = Date.now();
  const headquartersId = options.headquartersId && isStartingAirport(options.headquartersId)
    ? options.headquartersId
    : scenario.headquartersId;
  const headquarters = airportById(headquartersId);

  return {
    id: 'main',
    saveVersion: CURRENT_SAVE_VERSION,
    simulationVersion: SIMULATION_VERSION,
    seed,
    nextId: 2,
    createdAt: now,
    updatedAt: now,
    week: 1,
    cash: scenario.startingCash,
    reputation: scenario.startingReputation,
    companyName: randomCompanyName(seed),
    scenarioId: scenario.id,
    singlePlayerDifficulty: options.difficulty,
    quizModeEnabled: options.difficulty !== 'beginner',
    headquartersId,
    hubs: [{
      airportId: headquartersId,
      level: 1,
      weeklyCost: hubWeeklyCost(headquarters),
      builtWeek: 0,
      specialization: scenario.startingSpecialization,
    }],
    aircraft: [],
    routes: [],
    loans: [],
    events: [],
    reports: [],
    tutorialCompleted: false,
    quizCounter: 0,
    pendingQuiz: false,
    lastQuizWeek: 0,
    researchHistory: [],
    activeEffects: [],
    activeMarketEvents: [],
    marketEventResolutions: [],
    vouchers: {},
    assistants: { unlockedIds: [], dismissedIds: [], hired: [] },
    fuelInventory: { gallons: 0, averageCost: 0 },
    fuelFutures: [],
    fuelStorage: [],
    fuelStorageQueue: 0,
    fuelStorageBuiltThisWeek: 0,
    companyListing: { listed: false, listedWeek: 0, totalShares: 0, outstandingShares: 0, treasuryShares: 0, sharePrice: 0, lastValuation: 0, sharePriceHistory: [] },
    listingApplication: { active: false, weeksRemaining: 0, suspended: false, auditCompleted: false },
    playerAssets: { cash: 50_000_000, shares: 0, stockPortfolio: {}, stockPortfolioCost: {} },
    dividendRecords: [],
    ventureRounds: [],
    aiManagers: [],
    milestones: { achievedIds: [], lastCheckedWeek: 1 },
    victories: [],
    achievements: [],
    careerRecords: { peakFleetSize: 0, peakActiveRoutes: 0, peakHubCount: 1, totalPassengers: 0, acquiredAirlines: 0 },
    gameOver: undefined,
    ledger: [{
      id: 'ledger-1',
      week: 1,
      category: 'equity',
      cashAmount: 40_000_000,
      profitAmount: 0,
      label: '创始资本到账',
    }],
  };
}
